import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {OrganizationPermissionRole, TeamPermissionRole} from 'orgs/OrganizationConstants';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import getTestProps from 'test/lib/cypress/getTestProps';
import {AvatarSize, AvatarShape} from 'ui-library/avatar/AvatarConstants';
import {AvatarWithName} from 'ui-library/avatar/AvatarWithName';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER, NEAR_BLACK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {ToggleDisplayMode} from 'ui-library/floating-ui/Float';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {MenuWidth, MenuHeight, OptionData} from 'ui-library/menus/MenuConstants';
import OverflowMenu from 'ui-library/menus/OverflowMenu';
import {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {capwords, typeToName} from 'util/NameUtil';

import {CollaborationManagementStateQuery} from './__generated/CollaborationManagementStateQuery';
import {Policy} from './CollaborationManagementContent';
import {Policeable} from './useCollaborationManagementState';

type GqlParty = ExtractByTypename<
  CollaborationManagementStateQuery['policeable'],
  'EntryNode'
>['inheritedCollaborations'][number]['party'];

type GqlResource = NonNullable<
  ExtractByTypename<
    CollaborationManagementStateQuery['policeable'],
    'EntryNode'
  >['inheritedCollaborations'][number]['resource']
>;

export const NONE = 'None';

interface PolicyDropdownProps {
  allowNone: boolean;
  policies: Array<Policy>;
  unavailablePolicyIds?: Array<string>;
  isReadOnly: boolean;
  onChangePolicy: (policyId: string) => void;
  policyId: string | null;
  buttonSize?: ButtonSize;
}

export function PolicyDropdown(props: PolicyDropdownProps): JSX.Element {
  const policies = props.policies.filter(
    (policy) => policy.id === props.policyId || !props.unavailablePolicyIds?.includes(policy.id)
  );
  const options: Array<OptionData> = [
    ...(props.allowNone ? [{name: NONE, value: NONE}] : []),
    ...policies.map((p: Policy) => ({name: p.name, value: p.id})),
  ];
  const currentPolicyName = policies.find((policy) => policy.id === props.policyId)?.name ?? NONE;
  return (
    <SelectDropdown
      disabled={props.isReadOnly}
      buttonSize={props.buttonSize}
      tooltipToggleDisplayMode={ToggleDisplayMode.BLOCK}
      menu={
        <SearchableMenu
          width={MenuWidth.LARGE}
          height={MenuHeight.MEDIUM}
          optionDatas={options}
          onSelect={props.onChangePolicy}
          selectedValue={props.policyId}
        />
      }
      displayName={currentPolicyName}
      shouldEllipsize={true}
      dataTestAttrs={{component: 'SelectDropdown', element: 'input', key: 'policy'}}
    />
  );
}

interface InheritedPolicyInfoProps {
  hasTopSpacing: boolean;
  policyName: string;
  policeable: GqlResource;
}

export function InheritedPolicyInfo(props: InheritedPolicyInfoProps): JSX.Element {
  let icon: BenchlingIconName = 'lock';
  let name = 'No Access';
  let tooltipText = 'This policy is inherited from an item you do not have access to.';
  if (props.policeable.__typename !== 'InaccessibleInfoNode') {
    icon = props.policeable.type as BenchlingIconName;
    name = props.policeable.name;
    const policeableType =
      props.policeable.type === 'folder' ? 'a parent folder' : `the ${typeToName(props.policeable.type)}`;
    tooltipText = `This policy is inherited from ${policeableType}. Changes to this access must be done on that ${typeToName(props.policeable.type)}.`;
  }
  return (
    <div {...getTestProps({component: 'InheritedPolicyInfo', element: 'root'})}>
      <div className={classNames(props.hasTopSpacing && 'u-small-padding-top')}>{props.policyName}</div>
      <div className={classNames(css(styles.inheritedResourceRow))}>
        From{' '}
        <Tooltip text={tooltipText} placement={FloatingPlacement.BOTTOM}>
          <span className={classNames(css(styles.inheritedResource))}>
            <BenchlingIcon icon={icon} /> {name}
          </span>
        </Tooltip>
      </div>
    </div>
  );
}

export function OwnerPolicyInfo({
  policeable,
  policyName,
  hasTopSpacing,
}: {
  policeable: GqlResource | null;
  policyName?: string;
  hasTopSpacing: boolean;
}): JSX.Element {
  return (
    <>
      {policyName && <div className={classNames(hasTopSpacing && 'u-small-padding-top')}>{policyName}</div>}
      <div className={css(styles.inheritedResourceRow)}>
        From{' '}
        <span className={css(styles.inheritedResource)}>
          {policeable != null &&
            policeable.__typename !== 'InaccessibleInfoNode' &&
            `${capwords(typeToName(policeable.type))} `}
          Owner
        </span>
      </div>
    </>
  );
}

export type GroupRole = OrganizationPermissionRole | TeamPermissionRole;

export interface CollaborationRowProps {
  party: GqlParty;
  originatingPoliceables: Array<Policeable>;
  policyOptions: Array<Policy>;
  policyId: string;
  policeableIdToPolicyName: Map<string, string>;
  isDirectCollaborator: boolean;
  isReadOnly: boolean;
  adminPolicyExists?: boolean;
  groupRole?: GroupRole;
  onChangeCollaborationPolicy: (partyId: string, policyId: string, role?: GroupRole) => void;
  onRemoveCollaboration: (partyId: string, role?: GroupRole) => void;
  onAddCollaboration?: (partyId: string, role?: GroupRole) => void;
  renderExtraColumns?: ((partyId: string) => JSX.Element) | void;
  // onLeave is used when an actor or group admin wants to remove their (or their group's) non-admin access from an ownable
  onLeave?: (partyId: string) => void;
}

interface PartyCellProps {
  party: GqlParty;
  groupRole?: GroupRole;
}

export function PartyCell(props: PartyCellProps): JSX.Element {
  return (
    <div className={css(styles.partyCell)}>
      {props.groupRole === OrganizationPermissionRole.ADMIN ? (
        <div className={css(styles.groupAdminName)}>
          Admins of <strong>{getPartyDisplayString(props.party)}</strong>
        </div>
      ) : (
        <div className={css(styles.avatarWithName)}>
          <AvatarWithName
            avatarInfo={props.party}
            shape={props.groupRole != null ? AvatarShape.SQUARE : AvatarShape.CIRCLE}
            size={AvatarSize.NEWSMALL}
            showTooltip={false}
            wrapName={true}
          />
        </div>
      )}
    </div>
  );
}

interface OverflowMenuCellProps {
  menuOptions: Array<OverflowMenuOption>;
  isDirectCollaborator: boolean;
}

export function OverflowMenuCell(props: OverflowMenuCellProps): JSX.Element {
  return (
    <div>
      {props.menuOptions.length > 0 && props.isDirectCollaborator && (
        <OverflowMenu
          buttonProps={{buttonStyle: ButtonStyle.TRANSPARENT, size: ButtonSize.SMALL, textColor: NEAR_BLACK}}
          options={props.menuOptions}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  adminRowCell: {
    borderTop: 'none',
  },
  inheritedResourceRow: {
    fontSize: 11,
  },
  inheritedResource: {
    color: GRAY_DARKER,
    overflowWrap: 'break-word',
    wordWrap: 'break-word',
  },
  partyCell: {
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
    wordWrap: 'break-word',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  avatarWithName: {fontWeight: 'bold'},
  groupAdminName: {paddingLeft: 25, fontSize: 14},
});
